<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

import { makeUuid } from "~/utils/common";

const props = defineProps<{
  text: string;
  label?: string;
  editable?: boolean;
  autoedit?: boolean;
  clickToEdit?: boolean;
  dblclickToEdit?: boolean;
  padded?: boolean;
  noCursor?: boolean;
  textBase?: boolean;
  borderless?: boolean;
  invalid?: boolean;
  placeholder?: string;
  textSmall?: boolean;
  dataTestid?: string;
}>();

const emit = defineEmits<{
  update: [text: string];
  save: [text: string];
  click: [event: MouseEvent];
  focus: [event: FocusEvent];
  keydown: [event: KeyboardEvent];
}>();

const isEditing = ref(false);
const wrapper = ref<HTMLDivElement | null>(null);
const textInput = ref<HTMLInputElement | null>(null);
const persistFocus = ref(false);
const userClickedOutside = ref(false);

const onDocumentMouseDown = (event: MouseEvent) => {
  if (textInput.value && !textInput.value.contains(event.target as Node)) {
    userClickedOutside.value = true;
  }
};

const cleanupPersistFocus = () => {
  if (persistFocus.value) {
    document.removeEventListener("mousedown", onDocumentMouseDown, true);
    persistFocus.value = false;
    userClickedOutside.value = false;
  }
};

const currentText = ref(props.text);

const updateText = () => emit("update", currentText.value);

const refreshText = () => {
  currentText.value = props.text;
};

const startEditing = async (event?: MouseEvent | KeyboardEvent, options?: { persistFocus?: boolean }) => {
  if (!props.editable) {
    return;
  }
  if (event) {
    event.preventDefault();
  }

  if (isEditing.value) {
    if (event) {
      event.stopPropagation();
    }
    return;
  }

  isEditing.value = true;
  if (options?.persistFocus) {
    persistFocus.value = true;
    userClickedOutside.value = false;
    document.addEventListener("mousedown", onDocumentMouseDown, true);
  }

  // Needed because iOS won't open keyboard if readonly changed on the same tick
  nextTick(() => {
    textInput.value?.focus();
  });
};

const stopEditing = () => {
  if (!props.editable || !isEditing.value) {
    return;
  }
  isEditing.value = false;
  if (wrapper.value) {
    wrapper.value.scrollLeft = 0;
  }
  if (textInput.value) {
    textInput.value.blur();
  }
  emit("save", currentText.value);
};

const onBlur = () => {
  if (persistFocus.value && isEditing.value && textInput.value && document.contains(textInput.value)) {
    if (userClickedOutside.value) {
      cleanupPersistFocus();
    } else {
      nextTick(() => {
        if (isEditing.value && textInput.value && document.contains(textInput.value)) {
          textInput.value.focus();
        }
      });
      return;
    }
  }
  stopEditing();
};

const onInput = () => {
  cleanupPersistFocus();
  updateText();
};

const inputId = ref(`input-${makeUuid()}`);

const isReadonly = computed(() => !props.editable || !isEditing.value);

watch(
  () => props.text,
  async (newText) => {
    // set the value separately helps if the input is still initializing, not sure why
    await nextTick();
    currentText.value = newText;
  }
);

onMounted(() => {
  if (!props.autoedit) {
    return;
  }
  startEditing();
});

onUnmounted(() => {
  cleanupPersistFocus();
});

defineExpose({
  isEditing,
  refreshText,
  startEditing,
  stopEditing,
  isReadonly,
});
</script>

<template>
  <div
    ref="wrapper"
    class="flex h-full max-w-full items-center focus-ring-none"
    :class="isEditing && 'dart-no-drag'"
    @click="emit('click', $event)"
    @keydown="emit('keydown', $event)">
    <label :for="inputId" class="sr-only">{{ label }}</label>
    <input
      :id="inputId"
      ref="textInput"
      v-model="currentText"
      v-auto-width="{ minWidthPx: 40 }"
      type="text"
     
      :title="currentText"
      :readonly="isReadonly"
      :placeholder="placeholder"
      class="appearance-none truncate border bg-transparent focus-ring-none placeholder:text-app-light-text-vlt dark:placeholder:text-app-dark-text-vlt"
      :class="[
        {
          'rounded-md px-1.5 py-1': padded,
          'rounded px-px py-0': !padded,
          'text-sm': !textBase,
          'text-base': textBase,
          'caret-transparent select-none': !isEditing,
          'border-transparent focus:border-transparent': !isEditing || borderless,
          'dart-no-drag': isEditing,
          'border-primary-base focus:border-primary-base': !invalid && isEditing && !borderless,
          'border-danger-base focus:border-danger-base': Boolean(invalid) && isEditing && !borderless,
          'hover:border-oncolor': clickToEdit && !isEditing,
          'cursor-text': isEditing || clickToEdit,
          'cursor-default': noCursor && !isEditing && !clickToEdit,
          'cursor-pointer': !noCursor && !isEditing && !clickToEdit,
        },
      ]"
      @focus="emit('focus', $event)"
      @click="(event) => clickToEdit && startEditing(event)"
      @dblclick="(event) => dblclickToEdit && startEditing(event)"
      @input="onInput"
      @keydown.enter.esc.stop.prevent="stopEditing"
      @blur="onBlur" />
  </div>
</template>
